import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustry, faUsers, faBolt, faUserTie } from '@fortawesome/free-solid-svg-icons';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const About: React.FC = () => {
  const reasons = [
    {
      icon: faIndustry,
      title: "Industry Knowledge",
      description: "Our group is aware of hiring requirements and market trends in a number of industries.",
      color: "bg-primary"
    },
    {
      icon: faUsers,
      title: "Large Talent Network",
      description: "We have a wealth of qualified experts in our database that are eager to take advantage of new chances.",
      color: "bg-secondary"
    },
    {
      icon: faBolt,
      title: "Quick & Effective Hiring",
      description: "We save you time and effort by streamlining the hiring process.",
      color: "bg-accent"
    },
    {
      icon: faUserTie,
      title: "Tailored Approach",
      description: "We offer solutions tailored to your specific requirements, whether you're a business or a job seeker.",
      color: "bg-primary"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="section-title"
            >
              About Us
            </motion.h1>
            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="text-lg md:text-xl text-gray-700 mt-8"
            >
              At Skill Point Consultants, we think that the right work can change people's lives and that the right talent can propel companies to success.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Company Profile Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="order-2 lg:order-1"
            >
              <h2 className="text-3xl font-bold mb-6">Who We Are</h2>
              <p className="text-gray-700 mb-6">
                Skill Point Consultants is a leading recruitment and staffing firm in Gurgaon that helps companies find and hire top talent and helps job seekers land their ideal positions. We guarantee a flawless hiring experience because to our extensive network, years of experience, and profound comprehension of industry demands.
              </p>
              <p className="text-gray-700 mb-6">
                With a staff of knowledgeable HR consultants, our goal is straightforward: to help employers and job seekers succeed by connecting talent with opportunities. Skill Point Consultants is available to help you find the best people for your business or to help you change careers.
              </p>
              <p className="font-medium text-primary text-lg">
                📍 Based in Gurgaon | Serving Pan India
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="order-1 lg:order-2"
            >
              <div className="relative">
                <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/10 rounded-full filter blur-3xl"></div>
                <div 
                  className="w-full overflow-hidden shadow-xl rounded-2xl flex flex-col"
                  style={{ 
                    filter: "drop-shadow(0 20px 13px rgba(0, 0, 0, 0.2))",
                  }}
                >
                  {/* Company image */}
                  <div className="relative">
                    <img 
                      src="/images/connecting talent with profession.jpg" 
                      alt="Skill Point Consultants - Connecting Talent with Opportunities" 
                      className="w-full object-contain border-t-2 border-x-2 border-white/20 rounded-t-2xl"
                      style={{ 
                        filter: "contrast(1.1) brightness(1.05)"
                      }}
                    />
                  </div>
                  
                  {/* Text section with white background */}
                  <div className="bg-white p-4 text-center border-b-2 border-x-2 border-white/20 rounded-b-2xl">
                    <h3 className="text-xl md:text-2xl font-bold text-[#B01C1C] mb-1">Skill Point Consultants</h3>
                    <p className="text-md md:text-lg font-medium text-[#00A7D1]">Connecting Talent with Opportunities</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            Why Choose Skill Point Consultants?
          </motion.h2>
          
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center text-lg text-gray-700 max-w-3xl mx-auto mb-12"
          >
            For a customer-focused approach and just-in-time delivery, we are available wherever, at any time.
          </motion.p>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12"
          >
            {reasons.map((reason, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="card hover:shadow-xl transition-shadow duration-300"
              >
                <div className="p-8 text-center">
                  <div className={`mx-auto w-16 h-16 rounded-full ${reason.color} bg-opacity-10 flex items-center justify-center mb-6`}>
                    <FontAwesomeIcon 
                      icon={reason.icon} 
                      className={`text-2xl ${
                        reason.color === "bg-primary" ? "text-primary" : 
                        reason.color === "bg-secondary" ? "text-secondary" : "text-accent"
                      }`} 
                    />
                  </div>
                  <h3 className="text-xl font-bold mb-4">{reason.title}</h3>
                  <p className="text-gray-600">🔹 {reason.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section py-20 bg-gradient-to-r from-[#B01C1C] via-[#483D8B] to-[#00A7D1] text-white">
        <div className="container text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-6 text-white"
          >
            Ready to Partner With Us?
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-lg text-white/90 mb-10 max-w-3xl mx-auto"
          >
            Let's start building your team or advancing your career together.
          </motion.p>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Link to="/contact" className="btn bg-accent hover:bg-accent-light text-white">
              Contact Us Today
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About; 